import * as React from 'react';
import { Sparkles } from 'lucide-react';
import { TopNavItem } from '../TopNavItem';
import { SearchGlobal } from '../SearchGlobal';
import { Button } from '../Button';
import { AvatarLogoLockup } from '../AvatarLogoLockup';

export interface NavItem {
  /**
   * Unique identifier for the nav item
   */
  id: string;
  /**
   * Display label for the nav item
   */
  label: string;
  /**
   * Whether this nav item is currently active
   */
  active?: boolean;
}

export interface TopNavBarProps {
  /**
   * School logo image source URL
   */
  schoolLogoSrc?: string;
  /**
   * Alt text for school logo
   */
  schoolLogoAlt?: string;
  /**
   * Click handler for school logo
   */
  onSchoolLogoClick?: () => void;
  /**
   * Array of navigation items
   */
  navItems?: NavItem[];
  /**
   * Callback when a nav item is clicked
   */
  onNavItemClick?: (item: NavItem) => void;
  /**
   * Search value
   */
  searchValue?: string;
  /**
   * Search change handler
   */
  onSearchChange?: (value: string) => void;
  /**
   * Search submit handler
   */
  onSearchSubmit?: (value: string) => void;
  /**
   * Search clear handler
   */
  onSearchClear?: () => void;
  /**
   * Action button label (e.g., "Ask Arbor")
   */
  actionButtonLabel?: string;
  /**
   * Action button click handler
   */
  onActionButtonClick?: () => void;
  /**
   * Whether to show the action button
   */
  showActionButton?: boolean;
  /**
   * User avatar image source URL
   */
  avatarSrc?: string;
  /**
   * User avatar initials (fallback when no image)
   */
  avatarInitials?: string;
  /**
   * User avatar alt text
   */
  avatarAlt?: string;
  /**
   * Avatar section click handler
   */
  onAvatarClick?: () => void;
  /**
   * Logo section click handler
   */
  onLogoClick?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
}

/**
 * TopNavBar component - Arbor Design System
 *
 * A full-width navigation bar fixed to the top of the page.
 * Contains school logo, navigation items, search, action button, and avatar-logo lockup.
 */
export const TopNavBar = React.forwardRef<HTMLElement, TopNavBarProps>(
  (
    {
      schoolLogoSrc,
      schoolLogoAlt = 'School logo',
      onSchoolLogoClick,
      navItems = [],
      onNavItemClick,
      searchValue = '',
      onSearchChange,
      onSearchSubmit,
      onSearchClear,
      actionButtonLabel = 'Ask Arbor',
      onActionButtonClick,
      showActionButton = true,
      avatarSrc,
      avatarInitials,
      avatarAlt = 'User avatar',
      onAvatarClick,
      onLogoClick,
      className,
      style,
    },
    ref
  ) => {
    const containerStyles: React.CSSProperties = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '4px 8px',
      backgroundColor: '#ffffff',
      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      boxSizing: 'border-box',
      zIndex: 1000,
      height: '48px',
      ...style,
    };

    const leftSectionStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    };

    const schoolLogoContainerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '64px',
      height: '40px',
      backgroundColor: '#ffffff',
      border: '1px solid #efefef',
      borderRadius: '8px',
      overflow: 'hidden',
      padding: '2px',
      boxSizing: 'border-box',
      cursor: onSchoolLogoClick ? 'pointer' : 'default',
    };

    const schoolLogoImageStyles: React.CSSProperties = {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    };

    const placeholderStyles: React.CSSProperties = {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8f8f8',
      color: '#949494',
      fontSize: '10px',
      fontWeight: 500,
      textAlign: 'center',
    };

    const navItemsGroupStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    };

    const rightSectionStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    };

    const actionButtonStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    };

    const SchoolLogoElement = onSchoolLogoClick ? 'button' : 'div';

    return (
      <nav ref={ref} className={className} style={containerStyles}>
        {/* Left section: School logo + Nav items */}
        <div style={leftSectionStyles}>
          <SchoolLogoElement
            style={{
              ...schoolLogoContainerStyles,
              ...(onSchoolLogoClick ? { outline: 'none', border: '1px solid #efefef' } : {}),
            }}
            onClick={onSchoolLogoClick}
            {...(onSchoolLogoClick ? { type: 'button' } : {})}
          >
            {schoolLogoSrc ? (
              <img
                src={schoolLogoSrc}
                alt={schoolLogoAlt}
                style={schoolLogoImageStyles}
              />
            ) : (
              <div style={placeholderStyles}>Logo</div>
            )}
          </SchoolLogoElement>

          <div style={navItemsGroupStyles}>
            {navItems.map((item) => (
              <TopNavItem
                key={item.id}
                active={item.active}
                onClick={() => onNavItemClick?.(item)}
              >
                {item.label}
              </TopNavItem>
            ))}
          </div>
        </div>

        {/* Right section: Search + Action button + Avatar-Logo lockup */}
        <div style={rightSectionStyles}>
          <SearchGlobal
            value={searchValue}
            onChange={onSearchChange}
            onSubmit={onSearchSubmit}
            onClear={onSearchClear}
          />

          {showActionButton && (
            <Button
              variant="secondary"
              size="small"
              onClick={onActionButtonClick}
              style={actionButtonStyles}
            >
              <Sparkles size={16} strokeWidth={2} />
              {actionButtonLabel}
            </Button>
          )}

          <AvatarLogoLockup
            avatarSrc={avatarSrc}
            avatarInitials={avatarInitials}
            avatarAlt={avatarAlt}
            onAvatarClick={onAvatarClick}
            onLogoClick={onLogoClick}
          />
        </div>
      </nav>
    );
  }
);

TopNavBar.displayName = 'TopNavBar';
